/**
 * DateGroupingUtility - Utility for handling date and timestamp grouping for charts
 *
 * This utility provides methods for grouping timestamps by various intervals
 * and formatting dates for display in charts.
 */

export type DateGroupingInterval = 'day' | 'week' | 'month' | 'year';

export class DateGroupingUtility {
  /**
   * Group a timestamp by the specified interval
   */
  static groupTimestampByInterval(timestamp: string, interval: DateGroupingInterval): string {
    let date: Date | null = null;

    if (timestamp.includes('T') || (timestamp.includes('-') && timestamp.length >= 10)) {
      // ISO date string format (like "2025-06-15T03:15:24.856-07:00" or "2025-06-15")
      date = new Date(timestamp);
    } else if (/^\d{4}$/.test(timestamp)) {
      // Bare year string like "2024" — treat as Jan 1 of that year
      date = new Date(parseInt(timestamp, 10), 0, 1);
    } else if (/^\d{4}-\d{2}$/.test(timestamp)) {
      // Year-month string like "2024-06" — first day of month
      date = new Date(timestamp + '-01');
    } else if (/^\d+$/.test(timestamp)) {
      // Numeric string — treat as epoch ms if > 1e9, else epoch s
      const num = parseInt(timestamp, 10);
      date = new Date(num > 1e10 ? num : num * 1000);
    }

    // Check if the date is valid — fall back to the raw string so charts
    // still display something useful instead of "Invalid Date"
    if (!date || isNaN(date.getTime())) {
      console.warn('Unparseable date value, using raw label:', timestamp);
      return timestamp;
    }

    return DateGroupingUtility.formatDateByInterval(date, interval);
  }

  /**
   * Format a Date object by the specified interval
   */
  static formatDateByInterval(date: Date, interval: DateGroupingInterval): string {
    switch (interval) {
      case 'day':
        return date.toISOString().split('T')[0] || 'Invalid Date'; // YYYY-MM-DD

      case 'week': {
        const mondayDate = new Date(date);
        const dayOfWeek = date.getDay();
        const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Handle Sunday (0) as last day of week
        mondayDate.setDate(date.getDate() + daysToMonday);
        return `Week of ${mondayDate.toISOString().split('T')[0] || 'Invalid Date'}`;
      }

      case 'month':
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      case 'year':
        return String(date.getFullYear());

      default:
        return date.toISOString().split('T')[0] || 'Invalid Date';
    }
  }

  /**
   * Format date keys from OpenSearch for display
   */
  static formatDateForDisplay(dateKey: string, interval: DateGroupingInterval): string {
    if (dateKey.includes('Week of')) {
      return dateKey; // Already formatted for weeks
    }

    // For other formats, try to parse and format consistently
    try {
      const date = new Date(dateKey);
      if (isNaN(date.getTime())) {
        return dateKey; // Return as-is if can't parse
      }

      return DateGroupingUtility.formatDateByInterval(date, interval);
    } catch (error) {
      console.warn('Error formatting date:', dateKey, error);
      return dateKey;
    }
  }

  /**
   * Get calendar interval string for OpenSearch date histogram aggregations
   */
  static getCalendarInterval(interval: DateGroupingInterval): string {
    switch (interval) {
      case 'day':
        return '1d';
      case 'week':
        return '1w';
      case 'month':
        return '1M';
      case 'year':
        return '1y';
      default:
        return '1d';
    }
  }

  /**
   * Get date format string for OpenSearch date histogram aggregations
   */
  static getDateFormat(interval: DateGroupingInterval): string {
    switch (interval) {
      case 'day':
        return 'yyyy-MM-dd';
      case 'week':
        return "yyyy-'W'ww";
      case 'month':
        return 'yyyy-MM';
      case 'year':
        return 'yyyy';
      default:
        return 'yyyy-MM-dd';
    }
  }

  /**
   * Check if a date string represents a valid date
   */
  static isValidDate(dateString: string): boolean {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  }

  /**
   * Parse various timestamp formats into a Date object
   */
  static parseTimestamp(timestamp: string | number): Date | null {
    try {
      if (typeof timestamp === 'number') {
        return new Date(timestamp);
      }

      if (typeof timestamp === 'string') {
        // Try parsing as ISO string first
        if (timestamp.includes('T') || timestamp.includes('-')) {
          return new Date(timestamp);
        }

        // Try parsing as Unix timestamp
        const numericTimestamp = parseInt(timestamp);
        if (!isNaN(numericTimestamp)) {
          return new Date(numericTimestamp);
        }
      }

      return null;
    } catch (error) {
      console.warn('Error parsing timestamp:', timestamp, error);
      return null;
    }
  }

  /**
   * Get a human-readable label for a date grouping interval
   */
  static getIntervalLabel(interval: DateGroupingInterval): string {
    switch (interval) {
      case 'day':
        return 'Daily';
      case 'week':
        return 'Weekly';
      case 'month':
        return 'Monthly';
      case 'year':
        return 'Yearly';
      default:
        return 'Daily';
    }
  }

  /**
   * Get all available date grouping intervals
   */
  static getAvailableIntervals(): Array<{ key: DateGroupingInterval; label: string }> {
    return [
      { key: 'day', label: DateGroupingUtility.getIntervalLabel('day') },
      { key: 'week', label: DateGroupingUtility.getIntervalLabel('week') },
      { key: 'month', label: DateGroupingUtility.getIntervalLabel('month') },
      { key: 'year', label: DateGroupingUtility.getIntervalLabel('year') },
    ];
  }

  /**
   * Sort chart data points by date when category represents a date
   */
  static sortChartDataByDate<T extends { category: string }>(data: T[]): T[] {
    return data.sort((a, b) => {
      const dateA = DateGroupingUtility.parseTimestamp(a.category);
      const dateB = DateGroupingUtility.parseTimestamp(b.category);

      if (dateA && dateB) {
        return dateA.getTime() - dateB.getTime();
      }

      // Fallback to string comparison
      return a.category.localeCompare(b.category);
    });
  }
}
